'use client';

import { Dispatch, SetStateAction, useCallback, useEffect, useRef, useState } from 'react';
import { usePathname, useSearchParams } from 'next/navigation';
import { trackGtmEvent } from 'libs/gtm';
import type { AnalyticsProperties, TrackingFunction } from './types';
import { useAnalyticsModuleTrackingAttributes } from './AnalyticsModuleTracking';
import { getAdditionalTrackingProperties, getCookieValue } from './utils';
import { ANON_ID_COOKIE } from 'utils/cookies';
import { ANALYTICS_EVENTS } from './types';
import { trackVisitorIdSet } from './eventHandlerTracking';

const getAnonymousId = () => {
  if (typeof window === 'undefined') {
    return null;
  }

  return getCookieValue(ANON_ID_COOKIE) || null;
};

/**
 * Fires the 'Visitor ID Set' events (GTM dataLayer + event router) once per
 * page navigation.
 */
export default function VisitorIdTracker() {
  const [trackedAlready, setTrackedAlready] = useTrackedAlready(false);

  useEffect(() => {
    if (!trackedAlready) {
      trackGtmEvent(ANALYTICS_EVENTS.VISITOR_ID_SET, {
        source: 'self-generated',
        anonymous_id: getAnonymousId(),
      });

      trackVisitorIdSet({ source: 'self-generated' });

      setTrackedAlready(true);
    }
  }, [trackedAlready, setTrackedAlready]);

  return null;
}

/**
 * A hook that pushes tracking events into the GTM dataLayer (only events in
 * GTM_DESTINATION_EVENTS are forwarded — see libs/gtm.ts).
 * Can be configured to track automatically on route changes or manually via the returned function.
 *
 * @param {Object} props - The tracking event configuration
 * @param {string} props.eventName - The name of the event to track
 * @param {Object} props.properties - Properties/data to include with the tracking event
 * @param {boolean} [autoTrack=true] - Whether to automatically trigger tracking on route changes
 * @returns {Function} A function that can be called to manually trigger the tracking event
 *
 * @example
 * ```tsx
 * function CheckoutButton() {
 *   const trackPurchase = useGtmTrack({
 *     eventName: 'Purchase Completed',
 *     properties: { total: 99.99, currency: 'USD' }
 *   });
 *
 *   return <button onClick={trackPurchase}>Complete Purchase</button>;
 * }
 * ```
 */
export function useGtmTrack<T extends AnalyticsProperties>(
  props: { eventName: string; properties: T },
  autoTrack: boolean = false,
): TrackingFunction {
  const moduleAttributes = useAnalyticsModuleTrackingAttributes();
  const [trackedAlready, setTrackedAlready] = useTrackedAlready(false);

  const trackEvent = useCallback(
    (eventProperties: Record<string, unknown> = {}, callback?: () => void) => {
      if (!props.eventName) return;

      const properties = {
        ...props.properties,
        ...eventProperties,
        additional_data: [
          ...moduleAttributes,
          ...('additional_data' in props.properties && Array.isArray(props.properties.additional_data)
            ? props.properties.additional_data
            : []),
          ...(eventProperties && 'additional_data' in eventProperties && Array.isArray(eventProperties.additional_data)
            ? eventProperties.additional_data
            : []),
        ],
        ...getAdditionalTrackingProperties(),
      };

      trackGtmEvent(props.eventName, properties);
      callback?.();
    },
    [props.eventName, props.properties, moduleAttributes],
  );

  useEffect(() => {
    if (autoTrack && !trackedAlready) {
      trackEvent();
      setTrackedAlready(true);
    }
  }, [autoTrack, trackEvent, trackedAlready, setTrackedAlready]);

  return trackEvent;
}

function useTrackedAlready(initialValue: boolean): [boolean, Dispatch<SetStateAction<boolean>>] {
  const prevPathname = useRef('');
  const prevSearchParams = useRef('');
  const pathname = usePathname();
  const searchParams = useSearchParams();
  const [trackedAlready, setTrackedAlready] = useState(initialValue);

  useEffect(() => {
    // If the pathname or search params have changed, track the page view. This prevents URI fragments from triggering a re-track.
    if (prevPathname?.current !== pathname || prevSearchParams?.current !== searchParams?.toString()) {
      // eslint-disable-next-line react-hooks/set-state-in-effect -- resets tracking state on route change
      setTrackedAlready(false);
    }
    prevPathname.current = pathname;
    prevSearchParams.current = searchParams?.toString();
  }, [pathname, searchParams]);

  return [trackedAlready, setTrackedAlready];
}
